import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

export interface FlowErrorData {
  chartData: Array<{ date: string; count: number }>;
  summary: {
    total: number;
    today: number;
    yesterday: number;
    weeklyAverage: number;
    lastWeek?: number;
    thisWeek?: number;
    trend: 'increasing' | 'decreasing' | 'stable';
  };
}

interface FlowErrorTrendChartProps {
  flowErrorData: FlowErrorData | null;
}

const formatDate = (dateString: string) => {
  // Parse the date string as local time, not UTC
  const [year, month, day] = dateString.split('-').map(num => parseInt(num));
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export function FlowErrorTrendChart({ flowErrorData }: FlowErrorTrendChartProps) {
  if (!flowErrorData || flowErrorData.chartData.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Flow Errors - Last 14 Days</h3>
        <div className="text-center py-8 text-gray-500">
          <p>No error data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Flow Errors - Last 14 Days</h3>
      <div>
        <ResponsiveContainer width="100%" height={200}>
          <LineChart data={flowErrorData.chartData.slice(-14)}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="date" 
              tickFormatter={formatDate}
              tick={{ fontSize: 12 }}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              formatter={(value) => [`${value} errors`, 'Count']}
              labelFormatter={(label) => `Date: ${formatDate(label)}`}
            />
            <Line 
              type="monotone" 
              dataKey="count" 
              stroke="#EF4444" 
              strokeWidth={2}
              dot={{ fill: '#EF4444', r: 3 }}
            />
          </LineChart>
        </ResponsiveContainer>
        <div className="mt-4 flex justify-between text-sm">
          <div>
            <p className="text-gray-500">Total</p>
            <p className="font-semibold text-gray-900">{flowErrorData.summary.total}</p>
          </div>
          <div>
            <p className="text-gray-500">Last Week</p>
            <p className="font-semibold text-gray-900">{flowErrorData.summary.lastWeek || 0}</p>
          </div>
          <div>
            <p className="text-gray-500">This Week</p>
            <p className="font-semibold text-gray-900">{flowErrorData.summary.thisWeek || 0}</p>
          </div>
          <div>
            <p className="text-gray-500">Avg/Day</p>
            <p className="font-semibold text-gray-900">{flowErrorData.summary.weeklyAverage}</p>
          </div>
          <div>
            <p className="text-gray-500">Trend</p>
            <p className={`font-semibold ${
              flowErrorData.summary.trend === 'increasing' ? 'text-red-600' : 
              flowErrorData.summary.trend === 'decreasing' ? 'text-green-600' : 
              'text-gray-600'
            }`}>
              {(() => {
                const lastWeek = flowErrorData.summary.lastWeek || 0;
                const thisWeek = flowErrorData.summary.thisWeek || 0;
                if (lastWeek === 0 || thisWeek === lastWeek) return '→ Stable';
                const percentChange = Math.round(((thisWeek - lastWeek) / lastWeek) * 100);
                const arrow = thisWeek > lastWeek ? '↑' : '↓';
                return `${arrow} ${Math.abs(percentChange)}%`;
              })()}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}